import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Ship, Check, Loader2, FileDown, Calendar, ChevronDown, ChevronUp,
  CreditCard, Landmark, User, Hash
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_ARTICLES = [
  { num: 1, title: "Objet", summary: "Mise à disposition du catamaran pour exploitation commerciale", content: "Le Propriétaire met à disposition de l'Exploitant son catamaran pour une exploitation commerciale de croisières en Méditerranée pendant la durée définie au contrat." },
  { num: 2, title: "Désignation du navire", summary: "Caractéristiques et immatriculation du catamaran", content: "Le navire désigné au contrat est identifié par son nom, modèle, année de construction, numéro d'enregistrement et numéro WIN. Il est réputé conforme à la navigation." },
  { num: 3, title: "Durée", summary: "Période contractuelle et conditions de reconduction", content: "Le contrat couvre la période de saison définie. Le Capitaine récupère le navire à son port d'attache en début de saison et le ramène en fin de saison. Toute reconduction nécessite un accord écrit." },
  { num: 4, title: "Conditions financières", summary: "Rémunération forfaitaire et échéancier de paiement", content: "L'Exploitant verse au Propriétaire une rémunération forfaitaire selon l'échéancier défini. Les paiements sont effectués par virement bancaire. Tout retard peut entraîner la suspension du contrat." },
];

const COMMISSION_ARTICLE = { num: 5, title: "Rétrocession variable", summary: "Commission sur les passagers au-delà du seuil acquis", content: "En complément du forfait, une commission variable est versée au Propriétaire pour chaque passager au-delà du seuil acquis. Le taux, le seuil et le prix moyen de référence sont définis au contrat. Le versement intervient en fin de saison sur la base du décompte définitif." };

const REMAINING_ARTICLES_TITLES = [
  { title: "Utilisation du navire", content: "Le navire est exploité exclusivement dans le cadre de croisières commerciales en Méditerranée (Côte d'Azur, Corse et Sardaigne). Toute navigation hors zone nécessite l'accord écrit du Propriétaire." },
  { title: "Obligations de l'Exploitant", content: "L'Exploitant prend en charge : la commercialisation et gestion des clients, le carburant, l'équipage, l'avitaillement et l'entretien courant du navire." },
  { title: "Obligations du Propriétaire", content: "Le Propriétaire doit fournir un navire en parfait état, assurer les réparations majeures, maintenir une assurance valide et garantir la disponibilité du navire." },
  { title: "Entretien et réparations", content: "Entretien courant à la charge de l'Exploitant. Réparations majeures à la charge du Propriétaire. Toute panne doit être signalée immédiatement." },
  { title: "Assurances", content: "Le Propriétaire assure le navire (corps et machine). L'Exploitant dispose d'une assurance RC Professionnelle couvrant l'activité commerciale et les passagers." },
  { title: "Responsabilité", content: "L'Exploitant est responsable des dommages liés à l'exploitation, des passagers et de toute faute de navigation. Le Propriétaire reste responsable des vices cachés et défaillances structurelles." },
  { title: "État des lieux", content: "Un état des lieux détaillé est réalisé à la remise et à la restitution du navire. Toute dégradation imputable à l'Exploitant est à sa charge." },
  { title: "Indisponibilité du navire", content: "En cas d'indisponibilité prolongée : suspension des obligations financières ou ajustement proportionnel du montant dû." },
  { title: "Résiliation", content: "Résiliation possible pour faute (après mise en demeure de 15 jours), avec préavis de 30 jours, ou en cas de force majeure. Les conséquences financières varient selon la partie responsable." },
  { title: "Litiges", content: "Tout litige sera soumis aux tribunaux compétents de la juridiction définie au contrat." },
  { title: "Dispositions finales", content: "Le contrat constitue l'intégralité de l'accord. Toute modification nécessite un avenant écrit signé par les deux parties." },
];

function formatDateTimeFr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatMoney(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

export default function OwnerContractSignPage() {
  const { signToken } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showArticles, setShowArticles] = useState(false);
  const [showSignPad, setShowSignPad] = useState(false);
  const [openArticle, setOpenArticle] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  // RIB fields
  const [ribHolder, setRibHolder] = useState('');
  const [ribIban, setRibIban] = useState('');
  const [ribBic, setRibBic] = useState('');
  const [ribBank, setRibBank] = useState('');

  useEffect(() => {
    axios.get(`${API}/owner-contract/public/${signToken}`)
      .then(r => {
        setContract(r.data);
        if (r.data.owner_signed_at) setSigned(true);
        // Pre-fill RIB if already provided
        if (r.data.owner_rib) {
          setRibHolder(r.data.owner_rib.holder || '');
          setRibIban(r.data.owner_rib.iban || '');
          setRibBic(r.data.owner_rib.bic || '');
          setRibBank(r.data.owner_rib.bank || '');
        }
      })
      .catch(() => setError('Ce lien est invalide ou a expiré.'))
      .finally(() => setLoading(false));
  }, [signToken]);

  // Canvas drawing
  const startDraw = (e) => {
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0A2540';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleSign = async () => {
    if (!ribIban.trim()) { alert('Veuillez renseigner votre IBAN'); return; }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let hasDrawing = false;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 0) { hasDrawing = true; break; }
    }
    if (!hasDrawing) { alert('Veuillez dessiner votre signature'); return; }

    setSigning(true);
    try {
      const sigData = canvas.toDataURL('image/png');
      await axios.post(`${API}/owner-contract/public/${signToken}/sign`, {
        signature: sigData,
        rib_holder: ribHolder.trim(),
        rib_iban: ribIban.trim(),
        rib_bic: ribBic.trim(),
        rib_bank: ribBank.trim(),
      });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API}/owner-contract/public/${signToken}/pdf`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#C4724F] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center">
        <Ship className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (!contract) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A2540] to-[#0d3050]" data-testid="owner-contract-sign-page">
      {/* Header */}
      <div className="bg-[#0A2540] border-b border-white/10 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Ship className="h-8 w-8 text-[#C4724F] mx-auto mb-2" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">Contrat de Mise à Disposition</h1>
          <p className="text-white/50 text-sm mt-1">SOGNUDIMARE — Exploitation Commerciale Catamaran</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Contract info card */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="owner-contract-info-card">
          <div className="h-1.5 bg-gradient-to-r from-[#0A2540] via-[#0A2540] to-[#C4724F]" />
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-mono text-gray-400">{contract.reference}</span>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                signed || contract.status === 'signed' ? 'bg-green-100 text-green-700' :
                contract.status === 'signed_exploitant' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }`} data-testid="owner-contract-status">
                {signed || contract.status === 'signed' ? 'Signé' :
                 contract.status === 'signed_exploitant' ? 'En attente de votre signature' :
                 contract.status === 'sent' ? 'En attente de signatures' :
                 'Brouillon'}
              </span>
            </div>

            {/* Vessel */}
            <div className="bg-[#0A2540]/5 rounded-xl p-4 mb-4 border border-[#0A2540]/10">
              <div className="flex items-center gap-3">
                <Ship className="h-6 w-6 text-[#C4724F]" />
                <div>
                  <p className="font-bold text-[#0A2540] text-lg">{contract.vessel?.name}</p>
                  <p className="text-sm text-gray-500">{contract.vessel?.model} {contract.vessel?.year ? `(${contract.vessel.year})` : ''}</p>
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">Le Propriétaire</p>
                <p className="font-bold text-[#0A2540]">{contract.owner?.name}</p>
                {contract.owner?.address && <p className="text-sm text-gray-500">{contract.owner.address}</p>}
                {contract.owner_signed_at && (
                  <div className="flex items-center gap-1 mt-2">
                    <Check className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs text-green-700">Signé le {formatDateTimeFr(contract.owner_signed_at)}</span>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">L'Exploitant</p>
                <p className="font-bold text-[#0A2540]">{contract.exploitant?.name}</p>
                <p className="text-sm text-gray-500">{contract.exploitant?.company}</p>
                {contract.exploitant?.siret && <p className="text-xs text-gray-400">SIRET : {contract.exploitant.siret}</p>}
                {contract.exploitant_signed_at && (
                  <div className="flex items-center gap-1 mt-2">
                    <Check className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs text-green-700">Signé le {formatDateTimeFr(contract.exploitant_signed_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Period & Financial */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 pb-4 border-b">
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span>Saison <strong>{contract.season}</strong> : {contract.start_date} → {contract.end_date}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <CreditCard className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span>Montant total : <strong>{formatMoney(contract.total_amount)}</strong></span>
              </div>
            </div>

            {/* Payment schedule */}
            {contract.payment_schedule?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-[#0A2540] uppercase mb-2">Échéancier de paiement — Partie fixe</p>
                <div className="space-y-2">
                  {contract.payment_schedule.map((p, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${p.paid ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${p.paid ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-white'}`}>
                        {p.paid ? <Check className="h-3 w-3" /> : i + 1}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium">{formatMoney(p.amount)}</span>
                        <span className="text-xs text-gray-500 ml-2">{p.label}</span>
                      </div>
                      {p.paid && <span className="text-xs text-emerald-600">Payé</span>}
                    </div>
                  ))}
                </div>
                {/* Variable commission section */}
                {contract.commission_config?.enabled && (
                  <div className="mt-3 p-3 bg-[#C4724F]/5 border border-[#C4724F]/20 rounded-lg">
                    <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">Partie variable — Commission</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded-lg p-2 border">
                        <p className="text-base font-black text-[#C4724F]">{contract.commission_config.rate}%</p>
                        <p className="text-[10px] text-gray-500">Taux</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border">
                        <p className="text-base font-black text-[#0A2540]">{contract.commission_config.passenger_threshold}</p>
                        <p className="text-[10px] text-gray-500">Seuil passagers</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border">
                        <p className="text-base font-black text-emerald-600">{formatMoney((contract.commission_config.avg_price_per_passenger || 1990) * (contract.commission_config.rate || 10) / 100)}</p>
                        <p className="text-[10px] text-gray-500">Par pax suppl.</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Au-delà de {contract.commission_config.passenger_threshold} passagers, l'Exploitant verse {contract.commission_config.rate}% du CA par passager supplémentaire. Versement en fin de saison.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Articles summary */}
            <div>
              {(() => {
                const hasComm = contract.commission_config?.enabled;
                const articles = [...BASE_ARTICLES];
                let nextNum = 5;
                if (hasComm) {
                  articles.push({ ...COMMISSION_ARTICLE, num: nextNum });
                  nextNum = 6;
                }
                for (const item of REMAINING_ARTICLES_TITLES) {
                  articles.push({ num: nextNum, title: item.title, content: item.content });
                  nextNum++;
                }
                return (
                  <>
                    <button
                      onClick={() => setShowArticles(!showArticles)}
                      className="flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:text-[#C4724F] transition-colors w-full"
                      data-testid="toggle-owner-articles"
                    >
                      {showArticles ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      Articles du contrat ({articles.length} articles)
                    </button>

                    {showArticles && (
                      <div className="mt-3 space-y-1" data-testid="owner-articles-list">
                        {articles.map(a => {
                          const isOpen = openArticle === a.num;
                          return (
                            <div key={a.num} className="rounded-lg border overflow-hidden">
                              <button
                                onClick={() => setOpenArticle(isOpen ? null : a.num)}
                                className={`flex items-center gap-3 p-3 w-full text-left transition-colors ${isOpen ? 'bg-[#0A2540] text-white' : 'bg-gray-50 hover:bg-gray-100'}`}
                                data-testid={`article-toggle-${a.num}`}
                              >
                                <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center flex-shrink-0 font-bold ${isOpen ? 'bg-[#D4AF37] text-white' : 'bg-[#0A2540] text-white'}`}>
                                  {a.num}
                                </span>
                                <span className={`text-sm font-semibold flex-1 ${isOpen ? 'text-white' : 'text-gray-900'}`}>{a.title}</span>
                                {isOpen ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className={`h-4 w-4 flex-shrink-0 ${isOpen ? 'text-white' : 'text-gray-400'}`} />}
                              </button>
                              {isOpen && a.content && (
                                <div className="p-4 bg-white border-t text-sm text-gray-600 leading-relaxed" data-testid={`article-content-${a.num}`}>
                                  {a.content}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Download PDF */}
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={downloadPdf} className="gap-2" data-testid="download-owner-contract-pdf">
                <FileDown className="h-4 w-4" /> Télécharger le contrat complet (PDF)
              </Button>
            </div>
          </div>
        </div>

        {/* Signature section */}
        {!signed && contract.status !== 'signed' ? (
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="owner-signature-section">
            <div className="h-1.5 bg-[#C4724F]" />
            <div className="p-6">
              <h3 className="text-lg font-bold text-[#0A2540] mb-1">Signer le contrat</h3>
              <p className="text-sm text-gray-500 mb-4">
                En signant, vous acceptez la mise à disposition de votre navire <strong>{contract.vessel?.name}</strong> selon les conditions des {contract.commission_config?.enabled ? BASE_ARTICLES.length + 1 + REMAINING_ARTICLES_TITLES.length : BASE_ARTICLES.length + REMAINING_ARTICLES_TITLES.length} articles ci-dessus.
              </p>

              {!showSignPad ? (
                <Button
                  onClick={() => setShowSignPad(true)}
                  className="bg-[#C4724F] hover:bg-[#b5634a] w-full sm:w-auto"
                  data-testid="begin-owner-sign-btn"
                >
                  J'ai lu et j'accepte — Signer le contrat
                </Button>
              ) : (
                <div className="space-y-5">
                  {/* RIB Section */}
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-testid="rib-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Landmark className="h-4 w-4 text-blue-600" />
                      <h4 className="text-sm font-bold text-blue-900">Coordonnées bancaires (RIB)</h4>
                    </div>
                    <p className="text-xs text-blue-700 mb-3">
                      Pour recevoir vos paiements par virement, veuillez renseigner vos coordonnées bancaires.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-blue-800">Titulaire du compte</Label>
                        <Input
                          value={ribHolder}
                          onChange={e => setRibHolder(e.target.value)}
                          placeholder="Prénom NOM"
                          className="bg-white"
                          data-testid="rib-holder-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-800">Banque</Label>
                        <Input
                          value={ribBank}
                          onChange={e => setRibBank(e.target.value)}
                          placeholder="Nom de la banque"
                          className="bg-white"
                          data-testid="rib-bank-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-800">IBAN *</Label>
                        <Input
                          value={ribIban}
                          onChange={e => setRibIban(e.target.value)}
                          placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                          className="bg-white font-mono text-xs"
                          data-testid="rib-iban-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-800">BIC / SWIFT</Label>
                        <Input
                          value={ribBic}
                          onChange={e => setRibBic(e.target.value)}
                          placeholder="BNPAFRPPXXX"
                          className="bg-white font-mono text-xs"
                          data-testid="rib-bic-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Signature pad */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Votre signature</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        width={560}
                        height={180}
                        className="w-full touch-none cursor-crosshair"
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={endDraw}
                        onMouseLeave={endDraw}
                        onTouchStart={startDraw}
                        onTouchMove={draw}
                        onTouchEnd={endDraw}
                        data-testid="owner-signature-canvas"
                      />
                    </div>
                    <button onClick={clearCanvas} className="text-xs text-gray-400 hover:text-red-500 mt-1" data-testid="clear-owner-signature">
                      Effacer la signature
                    </button>
                  </div>

                  <Button
                    onClick={handleSign}
                    disabled={signing || !ribIban.trim()}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 w-full"
                    data-testid="confirm-owner-sign-btn"
                  >
                    {signing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Confirmer et signer le contrat
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="owner-signed-confirmation">
            <div className="h-1.5 bg-green-500" />
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Contrat signé</h3>
              <p className="text-gray-500 mb-4">
                Merci ! Votre contrat de mise à disposition pour le navire <strong>{contract.vessel?.name}</strong> a été signé. 
                Vous pouvez télécharger le PDF pour vos archives.
              </p>
              <Button variant="outline" onClick={downloadPdf} className="gap-2" data-testid="download-signed-owner-pdf">
                <FileDown className="h-4 w-4" /> Télécharger le contrat signé
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-white/30 text-xs pb-8">
          SOGNUDIMARE SARL — Croisières en catamaran — Corse & Méditerranée
        </p>
      </div>
    </div>
  );
}
